import React, { useState, useEffect } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import classNames from 'classnames';
import { Modal, PrimaryButton, IconCheckmark } from '../../components';

import css from './AmenityOptionsModal.module.css';

const AmenityOptionsModal = props => {
  const {
    isOpen,
    onClose,
    onConfirm,
    onCancel,
    amenity,
    selectedOptions: initialSelectedOptions,
    otherValue: initialOtherValue,
    className,
    rootClassName,
  } = props;

  const [selectedOptions, setSelectedOptions] = useState(initialSelectedOptions || []);
  const [otherValue, setOtherValue] = useState(initialOtherValue || '');

  // Update local state when modal opens or initialSelectedOptions changes
  useEffect(() => {
    if (isOpen) {
      setSelectedOptions(initialSelectedOptions || []);
      setOtherValue(initialOtherValue || '');
    }
  }, [isOpen, initialSelectedOptions, initialOtherValue]);

  const handleCheckboxChange = optionKey => {
    setSelectedOptions(prev => {
      if (prev.includes(optionKey)) {
        return prev.filter(key => key !== optionKey);
      } else {
        return [...prev, optionKey];
      }
    });
  };

  const handleConfirm = () => {
    onConfirm(selectedOptions, otherValue.trim());
    // Don't clear selectedOptions here - let parent state manage it
  };

  const handleCancel = () => {
    // Reset to initial values on cancel
    setSelectedOptions(initialSelectedOptions || []);
    setOtherValue(initialOtherValue || '');
    onCancel();
  };

  const classes = classNames(rootClassName || css.root, className);

  if (!amenity) {
    return null;
  }

  const options = amenity.options || [];
  const handleClose = () => {
    setSelectedOptions(initialSelectedOptions || []);
    setOtherValue(initialOtherValue || '');
    onClose();
  };
  return (
    <Modal
      id="AmenityOptionsModal"
      isOpen={isOpen}
      onClose={handleCancel}
      usePortal
      className={css.modal}
      // containerClassName={css.modalContainer}
      // contentClassName={css.modalContent}
      onManageDisableScrolling={()=>{}}
    >
      <div className={classes}>
        <div className={css.modalHeader}>
        <h2 className={css.title}>{amenity.name}</h2>
<button
            type="button"
            className={css.modalClose}
            onClick={handleClose}
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M1 1L14.3333 14.3333M1 14.3333L14.3333 1" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          </div>
        <div className={css.optionsList}>
          {options.map(option => {
            const optionKey = option.toLowerCase().replace(/\s+/g, '_');
            const isChecked = selectedOptions.includes(optionKey);
            const isOtherOption = optionKey === 'other';
            // Capitalize first letter of the option
            const displayLabel = option.charAt(0).toUpperCase() + option.slice(1);

            return (
              <div key={optionKey}>
                <label className={css.optionItem}>
                  <span className={css.checkboxLabel}>{displayLabel}</span>
                  <button
                    type="button"
                    className={classNames(css.checkbox, {
                      [css.checkboxSelected]: isChecked,
                    })}
                    onClick={() => handleCheckboxChange(optionKey)}
                    aria-label={`${isChecked ? 'Deselect' : 'Select'} ${option}`}
                    aria-pressed={isChecked}
                  >
                    {isChecked && <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_10498_1809)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M14.306 3.40738C14.4928 3.59491 14.5976 3.84921 14.5976 4.11438C14.5976 4.37954 14.4928 4.63385 14.306 4.82138L6.84087 12.3167C6.74221 12.4158 6.62509 12.4944 6.49618 12.548C6.36727 12.6016 6.2291 12.6292 6.08957 12.6292C5.95003 12.6292 5.81187 12.6016 5.68296 12.548C5.55405 12.4944 5.43692 12.4158 5.33827 12.3167L1.62926 8.59338C1.53413 8.50113 1.45825 8.39079 1.40606 8.26878C1.35386 8.14678 1.32638 8.01556 1.32523 7.88278C1.32408 7.75 1.34928 7.61832 1.39936 7.49542C1.44944 7.37253 1.5234 7.26087 1.61691 7.16698C1.71042 7.07309 1.82163 6.99884 1.94403 6.94856C2.06643 6.89827 2.19758 6.87297 2.32983 6.87413C2.46207 6.87528 2.59276 6.90287 2.71428 6.95528C2.83579 7.00768 2.94569 7.08387 3.03756 7.17938L6.08923 10.2434L12.8971 3.40738C12.9896 3.31445 13.0994 3.24073 13.2203 3.19044C13.3411 3.14014 13.4707 3.11426 13.6016 3.11426C13.7324 3.11426 13.862 3.14014 13.9828 3.19044C14.1037 3.24073 14.2135 3.31445 14.306 3.40738Z" fill="white"/>
</g>
<defs>
<clipPath id="clip0_10498_1809">
<rect width="15.9356" height="16" fill="white"/>
</clipPath>
</defs>
</svg>
}
                  </button>
                </label>

                {isOtherOption && isChecked && (
                  <input
                    type="text"
                    className={css.otherInput}
                    value={otherValue}
                    onChange={e => setOtherValue(e.target.value)}
                    // placeholder="Please enter"
                    maxLength={50}
                    aria-label="Specify other option"
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className={css.buttonGroup}>
          <PrimaryButton onClick={handleConfirm} className={css.saveButton}>
            Save Details
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

AmenityOptionsModal.defaultProps = {
  isOpen: false,
  selectedOptions: [],
  otherValue: '',
  className: null,
  rootClassName: null,
  amenity: null,
};

AmenityOptionsModal.propTypes = {
  isOpen: bool,
  onClose: func.isRequired,
  onConfirm: func.isRequired,
  onCancel: func.isRequired,
  amenity: shape({
    id: string.isRequired,
    name: string.isRequired,
    options: arrayOf(string),
  }),
  selectedOptions: arrayOf(string),
  otherValue: string,
  className: string,
  rootClassName: string,
};

export default AmenityOptionsModal;
